import { Autocomplete, Chip, CircularProgress, TextField } from "@mui/material";
import { useCallback, useMemo } from "react";
import { VirtualizedListboxComponent } from "./virtualized-listbox-component";

import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import type { RecipientPerson } from "../server/supabase-recipient-service";
import type { FC } from "react";

export interface AdditionalRecipientsProps {
  channel: ChannelName;
  onChange: (value: UUID[]) => void;
  error: string | string[] | undefined;
  disabled?: boolean;
  maxAutocompleteHeight?: number;
  value: UUID[];
  recipientsLoader: UseSupabaseLoaderState<RecipientPerson[]>;
}

const MAX_RECIPIENTS_BEFORE_VIRTUALIZED_LISTBOX = 100;

export const AdditionalRecipients: FC<AdditionalRecipientsProps> = ({
  channel,
  error,
  disabled,
  maxAutocompleteHeight,
  onChange,
  value: recipientIds,
  recipientsLoader: { data: recipients, loading, error: loadingError },
}) => {
  const recipientMap = useMemo(() => {
    const map = new Map<string, RecipientPerson>();
    recipients?.forEach((r) => map.set(r.id, r));
    return map;
  }, [recipients]);

  const value = useMemo(
    () =>
      recipientIds
        .map((id) => recipientMap.get(id))
        .filter((r): r is RecipientPerson => Boolean(r)),
    [recipientIds, recipientMap],
  );

  const optionKey = useCallback(
    (r: RecipientPerson): string => `${r.display} (${r.destination})`,
    [],
  );

  const optionLabel = useCallback((r: RecipientPerson): string => r.display ?? r.destination, []);

  const numRecipients = recipients?.length ?? 0;

  const slotProps = useMemo(
    () => ({
      listbox: {
        style:
          maxAutocompleteHeight === undefined
            ? undefined
            : { maxHeight: maxAutocompleteHeight, overflow: "auto" },
        component:
          numRecipients > MAX_RECIPIENTS_BEFORE_VIRTUALIZED_LISTBOX
            ? VirtualizedListboxComponent
            : undefined,
      },
    }),
    [maxAutocompleteHeight, numRecipients],
  );

  if (channel === null || channel === "personal_email" || loadingError) return null;

  return (
    <Autocomplete
      filterSelectedOptions
      multiple
      disablePortal
      id="additional-recipients-select"
      options={recipients || []}
      getOptionKey={optionKey}
      getOptionLabel={optionLabel}
      loading={!disabled && loading}
      disabled={disabled || loading}
      sx={{ minWidth: 200, pt: 2 }}
      value={value}
      slotProps={slotProps}
      onChange={(_, newValues) => onChange(newValues.map((v) => v.id))}
      renderValue={(tagValue, getTagProps, state) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option.id} label={state.getOptionLabel(option)} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Recipients"
          error={Boolean(error)}
          helperText={error}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              "data-testid": "additional-recipients-input",
            },
          }}
        />
      )}
    />
  );
};
